import { useEffect, useRef } from 'react';
import { Checkbox, Header } from 'semantic-ui-react';
import toast from 'react-hot-toast';
import { useMutation } from 'react-query';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { withForm } from '~/utils/withForm';
import { useForm, usePortfolioContext } from '~/hooks';
import { useUserContext } from '~/hooks/providers/UserContextProvider';
import { setCompletionState } from '~/api/projects';
import { DealStates, DevelopmentStages } from '~/constants';
import DealLifecycleStepper from '~/components/DealLifecycleStepper';

type CompletionCode = 'NONE' | 'DOCUMENTS_SIGNED' | 'CONSTRUCTION_COMPLETE' | 'DEAL_COMPLETE';

interface PortfolioCompletionStatesFormState {
  completionState: 'NONE' | 'DOCUMENTS_SIGNED' | 'CONSTRUCTION_COMPLETE' | 'DEAL_COMPLETE' | null;
}

// Completion state lives per-project on the server, so portfolio-level toggles fan out to every
// project in the portfolio whose dealState has reached diligence. Prefer winning-child projects
// (parentId set, post-bid-acceptance) when present; otherwise fall back to top-level projects.
function getDealProjects(projectBlobs: any[]) {
  const blobs = projectBlobs ?? [];
  const winningChildren = blobs.filter(
    (blob: any) => blob?.project?.parentId && blob?.projectState?.dealState >= DealStates.IN_DILIGENCE
  );
  if (winningChildren.length > 0) return winningChildren;
  return blobs.filter((blob: any) => blob?.projectState?.dealState >= DealStates.IN_DILIGENCE);
}

function getCanonicalDealBlob(projectBlobs: any[]) {
  const canonicalChild = projectBlobs?.find(
    (blob: any) => blob?.project?.parentId && blob?.projectState?.dealState >= DealStates.IN_DILIGENCE
  );
  const inDiligenceParent = projectBlobs?.find(
    (blob: any) => !blob?.project?.parentId && blob?.projectState?.dealState >= DealStates.IN_DILIGENCE
  );
  const originalParent = projectBlobs?.find((blob: any) => !blob?.project?.parentId);
  return canonicalChild ?? inDiligenceParent ?? originalParent;
}

const PortfolioCompletionStates = withForm({ defaults: {} }, () => {
  const { permissions } = usePermissions();
  const { portfolio, projectBlobs, portfolioState, fetchPortfolio } = usePortfolioContext();
  const { loggedInUser } = useUserContext();
  const viewerIsInvestor = !!loggedInUser?.isInvestor;
  const { formState, updateFormValue } = useForm<PortfolioCompletionStatesFormState>();

  const dealBlob = getCanonicalDealBlob(projectBlobs);
  const investorChildBlob = viewerIsInvestor
    ? projectBlobs?.find((blob: any) => blob?.project?.investorId)
    : undefined;
  const stepperBlob = viewerIsInvestor ? investorChildBlob : dealBlob;
  const stepperDealState = stepperBlob?.projectState?.dealState ?? portfolioState?.latestState;
  const stepperDeclined = viewerIsInvestor ? !!stepperBlob?.projectState?.isDeclined : false;

  const currentCode: CompletionCode | null = dealBlob?.project?.completionState?.code ?? null;
  const completionStateRef = useRef<string | null>(currentCode);

  // Use the first project's development stage to decide between the OPERATING (single "Deal complete")
  // checkbox and the two-step EXECUTED -> COMPLETED flow, matching the per-project component.
  const developmentStage = portfolio?.projects?.[0]?.developmentStage;
  const dealProjects = getDealProjects(projectBlobs);

  const { mutateAsync: updateCompletionState, status } = useMutation(async (code: CompletionCode) => {
    try {
      await Promise.all(dealProjects.map((blob: any) => setCompletionState(blob.project.id, code)));
      await fetchPortfolio?.();
      completionStateRef.current = code === 'NONE' ? null : code;
    } catch (err) {
      console.error(err);
      toast.error('Failed to update deal status');
      updateFormValue('completionState', completionStateRef.current);
    }
  });

  useEffect(() => {
    updateFormValue('completionState', currentCode);
    completionStateRef.current = currentCode;
  }, [currentCode]);

  const showActions = permissions.canSetProjectCompletionState && dealProjects.length > 0;

  return (
    <div className="card">
      <Header as='h2'>Deal status</Header>
      <DealLifecycleStepper
        dealState={stepperDealState}
        isDeclined={stepperDeclined}
        isInvestor={viewerIsInvestor}
      />
      {showActions && (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Header as='h3' style={{ marginTop: 'var(--medium)', marginBottom: 'var(--small)' }}>Actions</Header>
          {developmentStage === DevelopmentStages.OPERATING ? (
            <Checkbox
              label="Deal complete"
              style={{ margin: 'var(--small) 0' }}
              checked={formState.completionState === 'DEAL_COMPLETE'}
              onChange={() => {
                if (formState.completionState === 'DEAL_COMPLETE') {
                  updateFormValue('completionState', null);
                  updateCompletionState('NONE');
                } else {
                  updateFormValue('completionState', 'DEAL_COMPLETE');
                  updateCompletionState('DEAL_COMPLETE');
                }
              }}
              disabled={status === 'loading'}
            />
          ) : (
            <>
              <Checkbox
                label="Definitive documents signed"
                checked={!!formState.completionState}
                onChange={() => {
                  if (formState.completionState === 'DOCUMENTS_SIGNED') {
                    updateFormValue('completionState', null);
                    updateCompletionState('NONE');
                  } else {
                    updateFormValue('completionState', 'DOCUMENTS_SIGNED');
                    updateCompletionState('DOCUMENTS_SIGNED');
                  }
                }}
                disabled={status === 'loading'}
              />
              <Checkbox
                label="Construction complete"
                style={{ margin: 'var(--small) 0' }}
                checked={formState.completionState === 'CONSTRUCTION_COMPLETE'}
                onChange={() => {
                  if (formState.completionState === 'CONSTRUCTION_COMPLETE') {
                    updateFormValue('completionState', 'DOCUMENTS_SIGNED');
                    updateCompletionState('DOCUMENTS_SIGNED');
                  } else {
                    updateFormValue('completionState', 'CONSTRUCTION_COMPLETE');
                    updateCompletionState('CONSTRUCTION_COMPLETE');
                  }
                }}
                disabled={status === 'loading' || !formState.completionState}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
});

export default PortfolioCompletionStates;
